import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  CircularProgress, Link, InputAdornment, IconButton, Fade, Grow,
  useTheme, useMediaQuery,
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login({ email, password });

      // Route by role — no platform_admin in tenant deployments
      const TENANT_ROLES = ['tenant_admin', 'tenant_support', 'tenant_billing'];
      if (TENANT_ROLES.includes(user.role)) {
        navigate('/tenant');
      } else {
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e?.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 50%, #42A5F5 100%)',
        position: 'relative',
        overflow: 'hidden',
        p: 2,
      }}
    >
      {/* Background circles */}
      {[
        { top: '-10%', right: '-5%', size: { xs: 200, sm: 300 }, delay: '0s' },
        { bottom: '-10%', left: '-5%', size: { xs: 250, sm: 400 }, delay: '2s' },
      ].map((circle, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            ...circle,
            width: circle.size,
            height: circle.size,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
            animation: `float 6s ease-in-out ${circle.delay} infinite`,
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />
      ))}

      <Grow in timeout={800}>
        <Card
          elevation={24}
          sx={{
            maxWidth: { xs: '100%', sm: 440 },
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.98)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
              color: 'white',
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
            }}
          >
            <Fade in timeout={1000}>
              <Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    borderRadius: '50%',
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mb: 2,
                  }}
                >
                  <CarIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
                </Box>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, mb: 1 }}>
                  Fleet Management
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Sign in to your account
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Form */}
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            {error && (
              <Fade in>
                <Alert
                  severity="error"
                  sx={{ mb: 3, borderRadius: 2 }}
                  onClose={() => setError('')}
                >
                  {error}
                </Alert>
              </Fade>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email Address"
                placeholder="you@company.com"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                sx={{ mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                  boxShadow: '0 4px 12px rgba(13,71,161,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0c3d8f 0%, #1565C0 100%)',
                    boxShadow: '0 6px 16px rgba(13,71,161,0.4)',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{' '}
                <Link
                  component={RouterLink}
                  to="/register-company"
                  sx={{ fontWeight: 600, textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  Register your company
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grow>
    </Box>
  );
};

export default LoginPage;